import { affirmPaymentSchema } from '@/schema/paymentSchema';
import { Sku } from '@/types';
import { logger } from '@/core/logger';

export const validateAffirmBillingFields = (
  affirmBillingFields: {
    country: string;
    postalCode: string;
    state: string;
    city: string;
    address1: string;
  },
  onError: (errors: any) => void,
) => {
  try {
    affirmPaymentSchema.parse(affirmBillingFields);
  } catch (errors: any) {
    if (errors.errors) {
      onError(errors);
    }
  }
};

export const openAffirmModal = (
  payload: {
    customer: {
      address: {
        country: string;
        zipcode: string;
        state: string;
        city: string;
        line1: string;
        line2: string;
      };
      email: string;
      name: {
        first: string;
        last: string;
      };
      phone_number: string;
    };
    sku: Sku | null;
    studyId: string;
    finalPrice: number;
    study?: {
      location: {
        locationID: string;
        address: {
          name: string;
          city: string;
          state: string;
          country: string;
        };
        timezone: string;
      };
    };
  },
  onSuccess: (token: string) => void,
  onError: (error: any) => void,
) => {
  const { origin } = window.location;
  const { studyId, sku, customer, finalPrice, study } = payload;

  if (!sku?.skuID || !sku?.price.currency) {
    const error = new Error('Missing required SKU information');
    logger.error('Affirm initialization failed', { error: error.message, studyId });
    onError(error);
    return;
  }

  const logContext = {
    studyId,
    skuId: sku?.skuID,
    skuName: sku?.name,
    location: study ? {
      state: study.location.address.state,
      country: study.location.address.country,
    } : {
      state: customer.address.state,
      country: customer.address.country,
    },
    finalPrice,
    currency: sku?.price.currency,
    origin
  };

  logger.info('Opening Affirm modal', logContext);

  try {
    if (!window.affirm) {
      const error = new Error('Affirm script not loaded or not initialized');
      logger.error('Affirm initialization failed', { 
        ...logContext,
        error: error.message,
        windowAffirm: !!window.affirm
      });
      onError(error);
      return;
    }

    window.affirm.checkout({
      merchant: {
        user_confirmation_url: `${origin}/book/payment`,
        user_cancel_url: `${origin}/book/payment`,
        user_confirmation_url_action: 'POST',
        name: 'Prenuvo',
      },
      shipping: customer,
      billing: customer,
      items: [
        {
          display_name: 'Prenuvo Scan',
          sku: sku?.skuID,
          unit_price: finalPrice * 100,
          qty: 1,
          item_image_url: `${origin}/static/images/sku-torso.png`,
          item_url: `${origin}/static/images/sku-torso.png`,
        },
      ],
      metadata: {
        mode: 'modal',
      },
      order_id: studyId,
      currency: sku?.price.currency,
      shipping_amount: 0,
      tax_amount: 0,
      total: finalPrice * 100,
    });

    window.affirm.checkout.open({
      onFail: (error: any) => {
        if (error?.reason === 'canceled') {
          logger.info('Affirm checkout canceled by user', { 
            ...logContext,
            reason: error.reason
          });
        } else {
          logger.error('Affirm checkout failed', {
            ...logContext,
            errorType: error?.type,
            errorCode: error?.code,
            errorMessage: error?.message,
            statusCode: error?.status_code || error?.apiStatusCode,
            statusText: error?.status_text
          });
        }
        onError(error);
      },
      onSuccess: async ({ checkout_token }: { checkout_token: string }) => {
        logger.info('Affirm checkout successful', {
          ...logContext,
          tokenPrefix: checkout_token.substring(0, 4)
        });
        onSuccess(checkout_token);
      },
    });
  } catch (error) {
    logger.error('Affirm runtime error', {
      ...logContext,
      error: error instanceof Error ? error.message : String(error)
    });
    onError(error);
  }
};
